/**
 * «CONTRA QUÉ PLAGA», SUGERIDA EN EL ORIGEN (F9 del plan del cuaderno, 26-09-2026).
 *
 * La Orden APA/326/2007 (art. 3.1.a) pide en cada tratamiento «la plaga que lo
 * motivó», y en los clientes reales solo la lleva el 13,3 % de las líneas de
 * parte. La plaga llega a `spray_log_entries.pest_id / pest_name_snapshot` por
 * UN camino: `complete-treatment-task` copia la de la TAREA (`task.pest_id`) en
 * cada línea. Así que el sitio para arreglarlo es la tarea: al crearla y, si
 * sigue sin plaga, en la pantalla del trabajador antes de completar. La edge no
 * se toca (T2) y las pantallas que completan siguen mandando solo `task_id` (T4).
 *
 * Aquí solo se decide QUÉ se sugiere. Nunca se elige sola (D9: sugerida, un
 * toque, sin bloquear): una plaga puesta sin que nadie la mire iría al cuaderno
 * con apariencia de dato, y en el cuaderno un hueco se ve y se corrige; un dato
 * falso se firma.
 *
 * DE DÓNDE, por orden:
 *   1. el FOCO o PARTE de origen (`scouting_session_id` de la tarea): la plaga
 *      que clasificó el responsable y, detrás, las observadas, de más a menos
 *      individuos. Es lo que se vio en esa nave;
 *   2. la RECETA (`treatment_pest_map`): las plagas contra las que está
 *      configurada. Si son muchas, solo las primeras: el desplegable sigue ahí.
 *
 * PURO a propósito (sin red): lo usan la ficha de crear tarea y la pantalla del
 * trabajador, y las dos le pasan lo que ya han leído.
 */

export type PestSuggestionSource = "foco" | "receta";

export interface PestSuggestion {
  pestId: string;
  source: PestSuggestionSource;
}

/** Cuántas caben como botones antes de convertirse en otra lista. */
export const PEST_SUGGESTION_LIMIT = 3;

/** «Sin plaga» en los formularios: cadena vacía, «none» o null. */
export function hasPest(pestId: string | null | undefined): boolean {
  return !!pestId && pestId !== "none";
}

/**
 * Las plagas de una receta a partir del mapa que ya lee crear tarea
 * (plaga → recetas). El orden es el de `pestOrder` (la lista de plagas tal y
 * como se enseña, por nombre); las que no estén en ella van al final.
 */
export function recipePestIds(
  pestToTreatments: ReadonlyMap<string, ReadonlySet<string>>,
  treatmentId: string | null | undefined,
  pestOrder: readonly string[] = [],
): string[] {
  if (!treatmentId || treatmentId === "none") return [];
  const ids: string[] = [];
  for (const [pestId, treatments] of pestToTreatments) {
    if (treatments.has(treatmentId)) ids.push(pestId);
  }
  const rank = new Map(pestOrder.map((id, i) => [id, i]));
  return ids.sort((a, b) => (rank.get(a) ?? Infinity) - (rank.get(b) ?? Infinity));
}

/**
 * Las plagas de un parte, en el orden en que se sugieren: la clasificada primero
 * y luego las observadas de más a menos individuos, sin repetir.
 */
export function scoutingPestIds(
  triagePestId: string | null | undefined,
  observations: ReadonlyArray<{ pest_id: string | null | undefined; count?: number | null }>,
): string[] {
  const out: string[] = [];
  const push = (id: string | null | undefined) => {
    if (id && !out.includes(id)) out.push(id);
  };
  push(triagePestId);
  [...observations]
    .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
    .forEach((o) => push(o.pest_id));
  return out;
}

export interface SuggestTaskPestsInput {
  /** La plaga que ya tiene la tarea (o el formulario). Con una puesta, nada que sugerir. */
  currentPestId?: string | null;
  /** Del foco o parte de origen, ya ordenadas (`scoutingPestIds`). */
  focusPestIds?: readonly string[];
  /** De la receta elegida (`recipePestIds`). */
  recipePestIds?: readonly string[];
  /**
   * Las plagas que esta empresa puede elegir (las suyas y las globales). Una
   * sugerencia que no está en el desplegable no se podría enseñar con nombre;
   * sin la lista no se filtra.
   */
  knownPestIds?: ReadonlySet<string> | null;
  limit?: number;
}

/**
 * Las sugerencias, en orden y sin repetir. Vacío = no hay nada que sugerir, y
 * es lo normal (una receta sin plagas configuradas, una tarea sin parte): quien
 * llama no enseña nada, ni «sin sugerencias».
 */
export function suggestTaskPests(input: SuggestTaskPestsInput): PestSuggestion[] {
  if (hasPest(input.currentPestId)) return [];
  const limit = Math.max(0, input.limit ?? PEST_SUGGESTION_LIMIT);
  const out: PestSuggestion[] = [];
  const add = (ids: readonly string[] | undefined, source: PestSuggestionSource) => {
    for (const id of ids ?? []) {
      if (!hasPest(id)) continue;
      if (input.knownPestIds && !input.knownPestIds.has(id)) continue;
      if (out.some((s) => s.pestId === id)) continue;
      out.push({ pestId: id, source });
    }
  };
  add(input.focusPestIds, "foco");
  add(input.recipePestIds, "receta");
  return out.slice(0, limit);
}
